<template>
    <div class="family-tree-selector" ref="root">
        <input type="hidden" :name="inputName" :value="selectedId">

        <!-- Champ cliquable -->
        <div class="form-control d-flex align-items-center justify-content-between cursor-pointer"
             :class="{ 'is-invalid': invalid }"
             style="min-height: 38px; cursor: pointer;"
             @click="toggleDropdown">
            <span v-if="selectedFamily" class="text-dark" style="line-height: 1.4; word-break: break-word;">
                {{ selectedFamily.full_path || selectedFamily.name }}
            </span>
            <span v-else class="text-muted">
                {{ placeholder || 'Sélectionner une famille...' }}
            </span>
            <div class="d-flex align-items-center">
                <button v-if="selectedFamily" type="button" class="btn btn-sm p-0 text-danger mr-1" @click.stop="clearSelection" title="Effacer">
                    <i class="fal fa-times-circle"></i>
                </button>
                <i class="far fa-chevron-down text-muted"></i>
            </div>
        </div>

        <!-- Dropdown arborescent -->
        <div v-if="dropdownOpen" class="family-tree-dropdown" :style="dropdownStyle">
            <!-- Barre de recherche -->
            <div class="p-2 border-bottom">
                <input type="text"
                       class="form-control form-control-sm"
                       placeholder="Rechercher..."
                       v-model="searchQuery"
                       ref="searchInput">
            </div>

            <!-- Arbre -->
            <div class="family-tree-list">
                <div v-if="loading" class="text-center py-3">
                    <i class="far fa-circle-notch fa-spin text-muted"></i>
                </div>
                <div v-else-if="filteredFlatTree.length === 0" class="text-center py-3 text-muted small">
                    Aucune famille trouvée.
                </div>
                <div v-else>
                    <div v-for="node in filteredFlatTree"
                         :key="node.id"
                         class="family-tree-node"
                         :class="{ 'selected': selectedId === node.id, 'hovered': hoveredId === node.id }"
                         @click="selectFamily(node)"
                         @mouseenter="hoveredId = node.id"
                         @mouseleave="hoveredId = null">
                        <span v-for="d in node.depth" :key="'g-' + d" class="tree-indent"></span>
                        <span class="tree-chevron" v-if="node.children && node.children.length > 0" @click.stop="toggleNode(node.id)">
                            <i class="far fa-chevron-right" :style="{ transform: isExpanded(node.id) ? 'rotate(90deg)' : 'none' }"></i>
                        </span>
                        <span class="tree-chevron" v-else></span>
                        <span class="tree-nomenclature">{{ node.nomenclature }}</span>
                        <span class="tree-label" :class="'tree-depth-' + Math.min(node.depth, 2)">{{ node.name }}</span>
                    </div>
                </div>
            </div>

            <!-- Création rapide de hiérarchie -->
            <div class="family-tree-create border-top p-2">
                <div class="input-group input-group-sm">
                    <input type="text"
                           class="form-control form-control-sm"
                           placeholder="Créer : Ex : Électronique > Ordinateurs > Gaming"
                           v-model="newFamilyPath"
                           @keyup.enter="createFamily"
                           ref="createInput">
                    <div class="input-group-append">
                        <button class="ix-btn ix-btn-icon blue" type="button" @click="createFamily" :disabled="creating || !newFamilyPath.trim()" title="Créer la hiérarchie">
                            <i v-if="creating" class="far fa-circle-notch fa-spin"></i>
                            <i v-else class="far fa-plus"></i>
                        </button>
                    </div>
                </div>
                <small class="form-text text-muted mt-1" style="font-size: 11px;">
                    Séparateur <code>&gt;</code> pour une hiérarchie complète.
                </small>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'article-family-selector',
    props: {
        inputName: {
            type: String,
            default: 'article_family_id'
        },
        initialFamily: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: ''
        },
        invalid: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedFamily: null,
            families: [],
            loading: false,
            dropdownOpen: false,
            searchQuery: '',
            expandedNodeIds: new Set(),
            hoveredId: null,
            uniqueId: Math.random().toString(36).substr(2, 9),
            newFamilyPath: '',
            creating: false
        };
    },
    computed: {
        selectedId() {
            return this.selectedFamily ? this.selectedFamily.id : '';
        },
        flatTree() {
            const map = {};
            this.families.forEach(fam => {
                map[fam.id] = { ...fam, children: [] };
            });

            const roots = [];
            this.families.forEach(fam => {
                const node = map[fam.id];
                if (node.parent_id && map[node.parent_id]) {
                    map[node.parent_id].children.push(node);
                } else {
                    roots.push(node);
                }
            });

            const flat = [];
            const traverse = (node, depth, path, nomenclature) => {
                node.depth = depth;
                node.path = path;
                node.nomenclature = nomenclature;
                flat.push(node);
                if (node.children && node.children.length > 0) {
                    node.children.forEach((child, index) => {
                        traverse(child, depth + 1, [...path, node.id], nomenclature + '.' + (index + 1));
                    });
                }
            };

            roots.forEach((root, index) => {
                traverse(root, 0, [], String(index + 1));
            });

            return flat;
        },
        filteredFlatTree() {
            const query = this.searchQuery.trim().toLowerCase();

            if (!query) {
                return this.flatTree.filter(node => {
                    return node.path.every(parentId => this.expandedNodeIds.has(parentId));
                });
            }

            const matchingNodeIds = new Set();
            this.flatTree.forEach(node => {
                if (node.name.toLowerCase().includes(query)) {
                    matchingNodeIds.add(node.id);
                }
            });

            const nodesToShow = new Set();
            this.flatTree.forEach(node => {
                const hasMatchingAncestor = node.path.some(parentId => matchingNodeIds.has(parentId));
                const isAncestorOfMatch = this.flatTree.some(other =>
                    matchingNodeIds.has(other.id) && other.path.includes(node.id)
                );

                if (matchingNodeIds.has(node.id) || hasMatchingAncestor || isAncestorOfMatch) {
                    nodesToShow.add(node.id);
                }
            });

            return this.flatTree.filter(node => nodesToShow.has(node.id));
        },
        dropdownStyle() {
            return {};
        }
    },
    methods: {
        loadFamilies() {
            this.loading = true;
            fetch('/stocks/article-families/tree', {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => {
                if (!response.ok) throw new Error("Erreur de chargement");
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.families = data.families;
                    this.expandFirstLevel();
                }
                this.loading = false;
            })
            .catch(error => {
                console.error("Erreur de chargement des familles d'articles", error);
                this.loading = false;
            });
        },
        expandFirstLevel() {
            this.families.forEach(f => {
                if (!f.parent_id) {
                    this.expandedNodeIds.add(f.id);
                }
            });
        },
        isExpanded(id) {
            return this.expandedNodeIds.has(id);
        },
        toggleNode(id) {
            if (this.expandedNodeIds.has(id)) {
                this.expandedNodeIds.delete(id);
            } else {
                this.expandedNodeIds.add(id);
            }
            this.expandedNodeIds = new Set(this.expandedNodeIds);
        },
        toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen;
            if (this.dropdownOpen && this.families.length === 0) {
                this.loadFamilies();
            }
            if (this.dropdownOpen) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            }
        },
        selectFamily(item) {
            this.selectedFamily = item;
            this.dropdownOpen = false;
            this.searchQuery = '';
            this.$emit('change', item);
        },
        clearSelection() {
            this.selectedFamily = null;
            this.$emit('change', null);
        },
        async createFamily() {
            const path = this.newFamilyPath.trim();
            if (!path) return;

            this.creating = true;
            try {
                const response = await fetch('/stocks/article-families', {
                    method: 'POST',
                    ...this.getHttpJsonRequestHeaders(),
                    headers: {
                        ...this.getHttpJsonRequestHeaders().headers,
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || ''
                    },
                    body: JSON.stringify({ name: path, parent_id: null })
                });
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Erreur lors de la création.');
                }
                if (data.success) {
                    this.newFamilyPath = '';
                    await this.loadFamilies();
                    if (data.family) {
                        this.selectFamily(data.family);
                    }
                    if (window.showToast) {
                        window.showToast(data.message, 'success');
                    }
                }
            } catch (error) {
                if (window.showToast) {
                    window.showToast(error.message, 'error');
                }
            } finally {
                this.creating = false;
            }
        },
        handleClickOutside(e) {
            if (this.$refs.root && !this.$refs.root.contains(e.target)) {
                this.dropdownOpen = false;
            }
        }
    },
    mounted() {
        if (this.initialFamily) {
            this.selectedFamily = this.initialFamily;
        }
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    }
};
</script>

<style scoped>
.family-tree-selector {
    position: relative;
}

.cursor-pointer {
    cursor: pointer;
}

.family-tree-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 9999;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 2px;
}

.family-tree-list {
    max-height: 280px;
    overflow-y: auto;
    padding: 4px 0;
}

.family-tree-node {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 13px;
    color: #334155;
}

.family-tree-node:hover,
.family-tree-node.hovered {
    background-color: #f1f5f9;
}

.family-tree-node.selected {
    background-color: #dbeafe;
    color: #1e40af;
    font-weight: 600;
}

.tree-indent {
    display: inline-block;
    width: 20px;
    flex: 0 0 20px;
}

.tree-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    flex: 0 0 20px;
    color: #94a3b8;
    font-size: 11px;
    cursor: pointer;
}

.tree-nomenclature {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    color: #64748b;
    margin-right: 6px;
}

.tree-label.tree-depth-0 {
    font-weight: 700;
    color: #1e293b;
}

.tree-label.tree-depth-1 {
    font-weight: 600;
}

.is-invalid {
    border-color: #dc3545 !important;
}

.family-tree-create {
    background-color: #f8fafc;
    border-radius: 0 0 8px 8px;
}

.family-tree-create code {
    font-size: 11px;
}
</style>
